/**
 * Sign-in, sign-out and "who is signed in" — the flows tkt-back-office uses against the same
 * better-auth backend (lib/backOfficeApi.js), plus its optimistic-boot marker (lib/sessionMarker.js).
 *
 * THREE ANSWERS, NOT TWO. "The server said there is no session" and "the server could not be
 * asked" are different facts. Reading a dropped connection or a restarting backend as "signed out"
 * evicts an operator from work in progress; so `loadSession` answers authed / unauthed / unknown,
 * and `unknown` changes nothing on screen (mtn-support lib/sessionGate.ts states the same rule).
 */
import { API_BASE } from './api.js';
import { isLocalBackend } from './backendUrl.js';

/**
 * Every auth call has a deadline. A sign-in the server never answers must end in a message, not a
 * spinner that turns forever (measured: a local backend held POST /sign-in/email-otp open for
 * minutes while answering everything else in milliseconds).
 */
const AUTH_TIMEOUT_MS = 30_000;
const authFetch = async (path, init = {}) => {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), init.timeoutMs ?? AUTH_TIMEOUT_MS);
  if (init.signal) init.signal.addEventListener('abort', () => ctl.abort(), { once: true });
  try {
    return await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...init,
      signal: ctl.signal,
      headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...(init.headers || {}) },
    });
  } catch (e) {
    if (ctl.signal.aborted && !init.signal?.aborted) throw new Error('The server took too long to answer. Try again in a moment.');
    throw e;
  } finally {
    clearTimeout(timer);
  }
};

/** @returns {Promise<{status:'authed', user:object, session:object|null} | {status:'unauthed'} | {status:'unknown'}>} */
export async function loadSession({ signal } = {}) {
  let r;
  try {
    r = await authFetch('/api/auth/get-session', { signal });
  } catch {
    return { status: 'unknown' };
  }
  // 5xx and 429 are the server failing to answer, not answering "no".
  if (r.status >= 500 || r.status === 429) return { status: 'unknown' };
  if (!r.ok) return { status: 'unauthed' };
  const p = await r.json().catch(() => null);
  const user = p?.user || p?.session?.user || null;
  if (!user) return { status: 'unauthed' };
  return { status: 'authed', user, session: p?.session || null };
}

const failure = (data, r, fallback) => data?.error?.message || data?.message || (typeof data?.error === 'string' ? data.error : null) || fallback(r.status);

/** Step 1 of email-code sign-in. */
export async function sendSignInCode(email) {
  const r = await authFetch('/api/auth/email-otp/send-verification-otp', {
    method: 'POST',
    body: JSON.stringify({ type: 'sign-in', email: String(email || '').trim() }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || data?.error) throw new Error(failure(data, r, (s) => (s === 400 || s === 404 ? 'No account found for that email' : `Could not send the code (${s})`)));
  return data;
}

/** Step 2 of email-code sign-in. */
export async function verifySignInCode(email, code) {
  const r = await authFetch('/api/auth/sign-in/email-otp', {
    method: 'POST',
    body: JSON.stringify({ email: String(email || '').trim(), otp: String(code || '').trim() }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || data?.error) throw new Error(failure(data, r, (s) => (s === 400 || s === 401 ? 'That code is wrong or has expired' : `Sign-in failed (${s})`)));
  return data;
}

/**
 * Google: better-auth answers with Google's consent URL; Google returns to this deployment's
 * backend (backend.<apex>/api/auth/callback/google), which sets the session and sends the browser
 * back here. A failure comes back to this same page with `?error=…`.
 */
export async function signInWithGoogle() {
  // Back to this page WITH its query (an invitation's id rides in it), less a previous ?error.
  const query = new URLSearchParams(window.location.search);
  query.delete('error');
  const here = window.location.origin + window.location.pathname + (query.toString() ? `?${query}` : '');
  const r = await authFetch('/api/auth/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({ provider: 'google', callbackURL: here, errorCallbackURL: here }),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok || !data?.url) throw new Error(data?.message || 'Google sign-in is not available on this deployment');
  window.location.href = data.url;
}

export async function signOut() {
  clearSessionMarker();
  try { await authFetch('/api/auth/sign-out', { method: 'POST', body: '{}' }); } catch { /* the cookie is gone either way */ }
}

/**
 * LOCAL DEVELOPMENT ONLY: one-click sign-in with the backend's test account, whose email code is
 * fixed (tkt-backend CLAUDE.md). Only offered when this is a dev build AND the backend is
 * localhost, and only when both values are set in .env.local — never compiled into a real deploy.
 */
// The DEV test on each value, not only on the flag below: Vite inlines import.meta.env.* into the
// bundle and reads .env.local for production builds too, so a value referenced at module level
// shipped in the public JavaScript even though the button never showed. `DEV ? … : ''` folds to
// '' in a production build and the literal is dropped.
const DEV_EMAIL = import.meta.env.DEV ? import.meta.env.VITE_DEV_AUTH_EMAIL || '' : '';
const DEV_CODE = import.meta.env.DEV ? import.meta.env.VITE_DEV_AUTH_OTP || '' : '';
export const DEV_SIGN_IN_AVAILABLE = !!(import.meta.env.DEV && DEV_EMAIL && DEV_CODE && isLocalBackend());
export async function devSignIn() {
  if (!DEV_SIGN_IN_AVAILABLE) throw new Error('Developer sign-in is only available against a local backend');
  try {
    return await verifySignInCode(DEV_EMAIL, DEV_CODE);
  } catch {
    await sendSignInCode(DEV_EMAIL);
    return verifySignInCode(DEV_EMAIL, DEV_CODE);
  }
}

// ── Optimistic boot (tkt-back-office lib/sessionMarker.js) ──────────────────────────────────────
// Remember that this browser WAS signed in, paint the shell immediately, and let the real session
// answer arrive in the background. Not a credential: forging it gets an empty shell whose every
// request 401s. Only the operator's own name and email are read from it.
const MARKER_KEY = 'momo.backoffice.session';

export const readSessionMarker = () => {
  try {
    const m = JSON.parse(localStorage.getItem(MARKER_KEY) || 'null');
    return m && m.email ? m : null;
  } catch { return null; }
};
export const writeSessionMarker = (user) => {
  if (!user?.email) return;
  try { localStorage.setItem(MARKER_KEY, JSON.stringify({ email: user.email, name: user.name || '', at: Date.now() })); } catch { /* convenience only */ }
};
export const clearSessionMarker = () => { try { localStorage.removeItem(MARKER_KEY); } catch { /* nothing to forget */ } };

const LAST_EMAIL_KEY = 'momo.backoffice.lastEmail';
export const readLastEmail = () => { try { return localStorage.getItem(LAST_EMAIL_KEY) || ''; } catch { return ''; } };
export const rememberEmail = (email) => { try { if (email) localStorage.setItem(LAST_EMAIL_KEY, email); } catch { /* ignore */ } };
